
import React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PuttingResult } from "@/types/puttingResults";
import { Progress } from "@/components/ui/progress";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

interface TrainingStatisticsProps {
  results: PuttingResult[];
  level1Results: PuttingResult[];
  level2Results: PuttingResult[];
  level3Results: PuttingResult[];
}

const TrainingStatistics: React.FC<TrainingStatisticsProps> = ({
  results,
  level1Results,
  level2Results,
  level3Results
}) => {
  const { t } = useTranslation('training');
  
  // Calculs des statistiques globales
  const totalTests = results.length;
  const averagePoints = totalTests > 0 ? Math.round((results.reduce((sum, r) => sum + r.points, 0) / totalTests) * 10) / 10 : 0;
  const bestScore = totalTests > 0 ? Math.max(...results.map(r => r.points)) : 0;
  const worstScore = totalTests > 0 ? Math.min(...results.map(r => r.points)) : 0;

  // Statistiques par niveau
  const levelStats = [
    { level: t('trainingResults.statistics.level1'), count: level1Results.length, avg: level1Results.length > 0 ? Math.round((level1Results.reduce((sum, r) => sum + r.points, 0) / level1Results.length) * 10) / 10 : 0, color: "#10b981" },
    { level: t('trainingResults.statistics.level2'), count: level2Results.length, avg: level2Results.length > 0 ? Math.round((level2Results.reduce((sum, r) => sum + r.points, 0) / level2Results.length) * 10) / 10 : 0, color: "#3b82f6" },
    { level: t('trainingResults.statistics.level3'), count: level3Results.length, avg: level3Results.length > 0 ? Math.round((level3Results.reduce((sum, r) => sum + r.points, 0) / level3Results.length) * 10) / 10 : 0, color: "#8b5cf6" },
  ];

  // Distribution des scores
  const scoreDistribution = [
    { range: "0-3 pts", count: results.filter(r => r.points <= 3).length, color: "#ef4444" },
    { range: "4-7 pts", count: results.filter(r => r.points >= 4 && r.points <= 7).length, color: "#f59e0b" },
    { range: "8-11 pts", count: results.filter(r => r.points >= 8 && r.points <= 11).length, color: "#3b82f6" },
    { range: "12-15 pts", count: results.filter(r => r.points >= 12).length, color: "#10b981" },
  ];

  // Exercices les plus pratiqués
  const exerciseStats = results.reduce((acc, result) => {
    const exerciseName = result.test_name || result.test_id || "Standard";
    if (!acc[exerciseName]) {
      acc[exerciseName] = { count: 0, totalPoints: 0 };
    }
    acc[exerciseName].count++;
    acc[exerciseName].totalPoints += result.points;
    return acc;
  }, {} as Record<string, { count: number; totalPoints: number }>);

  const topExercises = Object.entries(exerciseStats)
    .map(([name, stats]) => ({
      name,
      count: stats.count,
      avgPoints: Math.round((stats.totalPoints / stats.count) * 10) / 10
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const getProgressPercentage = (score: number, max: number = 15) => {
    return (score / max) * 100;
  };

  if (totalTests === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500">
          <p>{t('trainingResults.statistics.noData')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques générales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">{t('trainingResults.statistics.totalTests')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-golf-600">{totalTests}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">{t('trainingResults.statistics.averagePoints')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-golf-600">{averagePoints} pts</div>
            <Progress value={getProgressPercentage(averagePoints)} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">{t('trainingResults.statistics.bestScore')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{bestScore} pts</div>
            <Progress value={getProgressPercentage(bestScore)} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">{t('trainingResults.statistics.worstScore')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{worstScore} pts</div>
            <Progress value={getProgressPercentage(worstScore)} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition par niveau */}
        <Card>
          <CardHeader>
            <CardTitle>{t('trainingResults.statistics.levelDistribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={levelStats.filter(stat => stat.count > 0)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ level, count, percent }) => `${level}: ${count} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {levelStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} tests`, name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Distribution des scores */}
        <Card>
          <CardHeader>
            <CardTitle>{t('trainingResults.statistics.scoreDistribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={scoreDistribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value} tests`, t('trainingResults.statistics.count')]} />
                  <Bar dataKey="count" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exercices les plus pratiqués */}
      <Card>
        <CardHeader>
          <CardTitle>{t('trainingResults.statistics.topExercises')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topExercises.map((exercise, index) => (
              <div key={exercise.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-golf-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium">{exercise.name}</div>
                    <div className="text-sm text-gray-500">{exercise.count} {t('trainingResults.statistics.testsCompleted')}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-golf-600">{exercise.avgPoints} pts</div>
                  <div className="text-sm text-gray-500">{t('trainingResults.statistics.average')}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Statistiques détaillées par niveau */}
      <Card>
        <CardHeader>
          <CardTitle>{t('trainingResults.statistics.performanceByLevel')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {levelStats.map((stat) => (
              <div key={stat.level} className="p-4 border rounded-lg">
                <h3 className="font-semibold text-lg mb-2" style={{ color: stat.color }}>
                  {stat.level}
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('trainingResults.statistics.tests')}:</span>
                    <span className="font-medium">{stat.count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('trainingResults.statistics.average')}:</span>
                    <span className="font-medium">{stat.avg} pts</span>
                  </div>
                  <Progress value={getProgressPercentage(stat.avg)} className="mt-2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TrainingStatistics;
